import { useEffect, useRef } from 'react';

const stats = [
  { value: '10+', label: 'Anos de Experiência', icon: 'ri-time-line' },
  { value: '500+', label: 'Casos Atendidos', icon: 'ri-briefcase-line' },
  { value: '2', label: 'Especialidades Jurídicas', icon: 'ri-scales-3-line' },
  { value: '100%', label: 'Dedicação ao Cliente', icon: 'ri-heart-line' },
];

export default function About() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-in-up').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="sobre" ref={sectionRef} className="py-28 lg:py-36 bg-ice overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center mb-20">
          {/* Left */}
          <div
            className="fade-in-up"
            style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-gold" />
              <span className="text-gold font-sans font-600 text-xs tracking-[0.25em] uppercase">Institucional</span>
            </div>
            <h2 className="font-serif text-deep text-4xl lg:text-5xl font-700 leading-tight mb-8">
              Excelência Jurídica<br />
              <span className="text-graphite font-400 italic">em cada detalhe</span>
            </h2>
            {/* Decorative line */}
            <div className="flex items-center gap-3 mb-8">
              <span className="w-16 h-0.5 bg-gold" />
              <span className="w-3 h-3 rounded-full border-2 border-gold" />
            </div>
            {/* Photo */}
            <div className="relative rounded-lg overflow-hidden">
              <img
                src="https://storage.readdy-site.link/project_files/a8985741-0b59-4acc-bd5d-746de6a1438f/38bce003-a99f-4b0f-9564-1cf81ef83276_benner-martins.png?v=be283a1c806d7b51cd5ba81894ce65e5"
                alt="Dr. Benner Fernando Ribeiro Martins - Advogado Especializado São Luís"
                className="w-full h-auto block"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-deep/60 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <p className="font-serif text-gold text-lg font-600">Dr. Benner Fernando Ribeiro Martins</p>
                <p className="font-sans text-ice/80 text-sm mt-1">Advogado Especialista</p>
              </div>
            </div>
          </div>

          {/* Right */}
          <div
            className="fade-in-up"
            style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}
          >
            <p className="font-sans text-graphite text-lg font-600 leading-relaxed mb-6">
              O escritório <strong className="text-deep">Benner Martins Advocacia Especializada</strong> atua com rigor técnico e comprometimento ético na defesa dos direitos de seus clientes.
            </p>
            <p className="font-sans text-graphite/80 text-base font-300 leading-loose mb-6">
              Fundado pelo Dr. Benner Fernando Ribeiro Martins, o escritório se destaca pela atuação estratégica e individualizada, oferecendo assessoria jurídica de alto nível nas áreas do <strong>Direito Penal</strong> e <strong>Direito Previdenciário</strong>, sempre respeitando os princípios éticos da OAB.
            </p>
            <p className="font-sans text-graphite/80 text-base font-300 leading-loose mb-8">
              Com sede em São Luís — MA, no Edifício Century Multiempresarial, o escritório oferece um ambiente reservado, moderno e acolhedor, onde cada cliente recebe atenção personalizada e dedicada, independentemente da complexidade da causa.
            </p>
            {/* Values */}
            <div className="space-y-4">
              {['Ética profissional e transparência em cada etapa', 'Estratégia jurídica construída caso a caso', 'Comunicação clara e atendimento humanizado'].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-checkbox-circle-fill text-gold" />
                  </span>
                  <span className="font-sans text-graphite text-sm font-400">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 pt-12 border-t border-graphite/10">
          {stats.map((stat, i) => (
            <div
              key={stat.value}
              className="fade-in-up text-center py-8 px-4"
              style={{
                opacity: 0,
                transform: 'translateY(30px)',
                transition: `opacity 0.8s ease ${i * 0.15}s, transform 0.8s ease ${i * 0.15}s`,
              }}
            >
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <i className={`${stat.icon} text-2xl text-gold`} />
              </div>
              <p className="font-serif text-deep text-4xl lg:text-5xl font-700 leading-none mb-2" style={{ color: '#C8A24A' }}>
                {stat.value}
              </p>
              <p className="font-sans text-graphite text-sm font-400 leading-tight">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
